/**
 * 计费重构（2026-06-28）— 余额不足付费墙。
 *
 * 旧的「订阅过期 / 5h 配额超限」两态已删。预付费钱包模型下只有一种
 * 拦截：余额用完（402 + kind=insufficient_balance）。api.ts 的 startTurn
 * 把它 dispatch 成 PAYWALL_EVENT，App.tsx 持有 open/close 并把 PaywallInfo
 * 传进来。
 *
 * 单一引导动作：充值。点 "Top up" 在弹窗内部自渲染 <TopupModal>，不依赖
 * 外部 onManage —— 调用方只需 onClose。
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PaywallInfo } from '../api';
import { TopupModal } from './TopupModal';

interface Props {
  info: PaywallInfo;
  onClose: () => void;
}

export function PaywallModal({ info, onClose }: Props) {
  const { t } = useTranslation();
  const [topupOpen, setTopupOpen] = useState(false);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(26, 24, 20, 0.45)',
          display: 'grid',
          placeItems: 'center',
          zIndex: 1000,
          padding: 20,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'var(--paper-0)',
            border: '1px solid var(--ink-0)',
            borderRadius: 6,
            maxWidth: 460,
            width: '100%',
            padding: '28px 28px 24px',
            boxShadow: '6px 6px 0 0 rgba(26,24,20,0.12)',
          }}
        >
          <div
            className="mono"
            style={{
              fontSize: 10.5,
              letterSpacing: '.16em',
              fontWeight: 700,
              color: 'var(--red, #d23f3f)',
              marginBottom: 12,
            }}
          >
            BALANCE
          </div>

          <h2
            className="serif"
            style={{
              fontSize: 26,
              fontWeight: 500,
              lineHeight: 1.15,
              margin: '0 0 14px',
              color: 'var(--ink-0)',
              letterSpacing: '-.01em',
            }}
          >
            {t('billing.insufficientTitle')}
          </h2>

          <p
            style={{
              fontSize: 14.5,
              lineHeight: 1.6,
              color: 'var(--ink-1)',
              margin: '0 0 20px',
            }}
          >
            {t('billing.insufficientBody')}
            {info.detail ? (
              <span style={{ display: 'block', marginTop: 8, color: 'var(--ink-2)' }}>
                {info.detail}
              </span>
            ) : null}
          </p>

          <div
            style={{
              display: 'flex',
              gap: 10,
              justifyContent: 'flex-end',
              flexWrap: 'wrap',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              className="mono"
              style={{
                fontSize: 11,
                letterSpacing: '.12em',
                fontWeight: 700,
                padding: '10px 16px',
                border: '1px solid var(--rule-s)',
                background: 'transparent',
                color: 'var(--ink-2)',
                cursor: 'pointer',
              }}
            >
              {t('billing.notNow')}
            </button>
            <button
              type="button"
              onClick={() => setTopupOpen(true)}
              className="mono"
              style={primaryStyle()}
            >
              {t('billing.topUp')}
            </button>
          </div>
        </div>
      </div>

      <TopupModal open={topupOpen} onClose={() => setTopupOpen(false)} />
    </>
  );
}

function primaryStyle(): React.CSSProperties {
  return {
    fontSize: 11,
    letterSpacing: '.12em',
    fontWeight: 700,
    padding: '10px 16px',
    border: '1px solid var(--ink-0)',
    background: 'var(--ink-0)',
    color: 'var(--paper-0)',
    cursor: 'pointer',
  };
}
